import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { useTheme } from '@voilajsx/uikit/theme-provider';
import { Button } from '@voilajsx/uikit/button';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';

const UsingThemes = () => {
  const { theme, setTheme, availableThemes } = useTheme();

  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Using Themes</h1>
          <p className="text-lg text-muted-foreground">
            UIKit includes 5 professionally designed themes. Learn how to use and switch between them.
          </p>
        </div>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Available Themes</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'default', desc: 'Clean and neutral', primary: 'Slate blue' },
              { name: 'ocean', desc: 'Cool and professional', primary: 'Ocean blue' },
              { name: 'forest', desc: 'Natural and calm', primary: 'Forest green' },
              { name: 'sunset', desc: 'Warm and energetic', primary: 'Sunset orange' },
              { name: 'midnight', desc: 'Deep and dramatic', primary: 'Violet purple' },
            ].map((t) => (
              <Card
                key={t.name}
                className={`cursor-pointer transition-all ${
                  theme === t.name ? 'ring-2 ring-primary' : 'hover:border-primary/50'
                }`}
                onClick={() => setTheme(t.name as typeof theme)}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg capitalize flex items-center justify-between">
                    {t.name}
                    {theme === t.name && (
                      <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                        Active
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{t.desc}</p>
                  <p className="text-xs text-muted-foreground">Primary: {t.primary}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-sm text-muted-foreground">
            Click any card above to switch themes and see the change live.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Setting Up ThemeProvider</h2>

          <CodePreview
            title="main.tsx"
            code={`import { ThemeProvider } from '@voilajsx/uikit/theme-provider';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider
      defaultTheme="default"  // or "ocean", "forest", "sunset", "midnight"
      defaultMode="light"     // or "dark"
    >
      <App />
    </ThemeProvider>
  </React.StrictMode>
);`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Switching Themes Programmatically</h2>

          <CodePreview
            title="useTheme Hook"
            code={`import { useTheme } from '@voilajsx/uikit/theme-provider';

function ThemeSwitcher() {
  const { theme, setTheme, availableThemes } = useTheme();

  return (
    <select
      value={theme}
      onChange={(e) => setTheme(e.target.value)}
    >
      {availableThemes.map((t) => (
        <option key={t} value={t}>
          {t.charAt(0).toUpperCase() + t.slice(1)}
        </option>
      ))}
    </select>
  );
}`}
            language="tsx"
          />

          <div className="p-4 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground mb-3">Try it - current theme: <strong>{theme}</strong></p>
            <div className="flex flex-wrap gap-2">
              {availableThemes.map((t) => (
                <Button
                  key={t}
                  variant={theme === t ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTheme(t)}
                  className="capitalize"
                >
                  {t}
                </Button>
              ))}
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">useTheme Hook API</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Property</th>
                  <th className="text-left p-3 font-semibold">Type</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">theme</td>
                  <td className="p-3 text-muted-foreground">string</td>
                  <td className="p-3 text-muted-foreground">Current theme name</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">setTheme</td>
                  <td className="p-3 text-muted-foreground">function</td>
                  <td className="p-3 text-muted-foreground">Change the theme</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">availableThemes</td>
                  <td className="p-3 text-muted-foreground">string[]</td>
                  <td className="p-3 text-muted-foreground">List of all themes</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">mode</td>
                  <td className="p-3 text-muted-foreground">'light' | 'dark'</td>
                  <td className="p-3 text-muted-foreground">Current color mode</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">toggleMode</td>
                  <td className="p-3 text-muted-foreground">function</td>
                  <td className="p-3 text-muted-foreground">Toggle light/dark</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">setMode</td>
                  <td className="p-3 text-muted-foreground">function</td>
                  <td className="p-3 text-muted-foreground">Set specific mode</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Theme Persistence</h2>
          <p className="text-muted-foreground">
            Theme selection is automatically saved to localStorage and restored on page load.
          </p>

          <CodePreview
            title="Storage Keys"
            code={`// Stored automatically:
localStorage.getItem('uikit-theme')  // "default", "ocean", etc.
localStorage.getItem('uikit-mode')   // "light" or "dark"`}
            language="javascript"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Next Steps</h2>
          <p className="text-muted-foreground">
            In the next lesson, we'll learn how to implement dark mode for your application.
          </p>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default UsingThemes;
